import React, { useState } from "react";
import { Eye, EyeOff, ArrowLeft, Mail, Lock, UserRound, CircleAlert } from "lucide-react";
import { motion } from "framer-motion";
import { loginUser, useAppDispatch } from "../../../redux";
import { LoginErrorsType } from "../../shared/model/AuthInterfaces";
import CommonLoginLeftPart from "./CommonLoginLeftPart";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { RoleEnum } from "../../shared/model/role.enum";
import { StatusEnum } from "../../shared/model/status.enum";
import SocialLogins from "./SocialLogins";
import { GlobalService } from "../../shared";
import Card, { CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import Spinner from "../../shared/components/Spinner";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector";
import { createUserNotificationAction } from "../../../redux/Actions/UserNotificationActions";
import { NotificationTypeEnum, NotificationStatusEnum } from "../../shared/model/user-notification.enum";

const LoginComponent = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });
    const isLoading = useSelector(selectLoaderValue);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<LoginErrorsType>({});
    const dispatch = useAppDispatch();

    const validate = () => {
        const newErrors: LoginErrorsType = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.email) {
            newErrors.email = t("login.emailRequired");
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = t("login.invalidEmail");
        }

        if (!formData.password || formData.password.length < 8) {
            newErrors.password = t("login.passwordRequired");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Helper function to create promotional notification
    const createPromotionalNotification = async (userId: string) => {
        try {
            await createUserNotificationAction({
                user: { id: userId },
                type: { id: NotificationTypeEnum["Banner"] },
                status: { id: NotificationStatusEnum.Unread },
                title: "🎁 Free Promotional Quotes Available!",
                message: "You've received a free quote as a welcome gift! Contact the customer now and make your best offer!",
                url: "/professionals"
            });
            console.log("✅ Promotional notification created successfully");
        } catch (error) {
            console.error("Failed to create promotional notification:", error);
        }
    };

    const handleSubmit = async e => {
        if (!isLoading) {
            e.preventDefault();
            if (validate()) {
                const resultAction = await dispatch(loginUser(formData));
                const payload: any = resultAction?.payload;

                if (payload?.user) {
                    const { status, role, phone, id }: any = payload.user;
                    // If login is successful and user is inactive (id == 2), redirect
                    if (Number(status?.id) === StatusEnum.inactive) {
                        navigate("/auth/confirm-email");
                    } else if (Number(status?.id) === StatusEnum.active) {
                        if (Number(role?.id) === RoleEnum.professional || Number(role?.id) === RoleEnum.internationl_professional) {
                            if (!phone) {
                                // Create notification record
                                createPromotionalNotification(id);
                                window.location.href = "/myprofile";
                            } else {
                                window.location.href = "/professionals";
                            }
                        } else if (Number(role?.id) === RoleEnum.client) {
                            console.log("phone", phone);
                            if (!phone) {
                                window.location.href = "/client/profile";
                            } else {
                                window.location.href = "/clients";
                            }
                        }
                        else if (Number(role?.id) === RoleEnum.admin) {
                            window.location.href = "/admin/dashboard";
                        }
                    }
                }
            }
        } else {
            GlobalService.showToast("info", t("login.pleaseWait"), t("login.processingRequest"));
        }
    };

    const handleChange = e => {
        const { name, type, checked, value } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 flex flex-col p-4">
            {/* Navigation Header */}
            <div className="max-w-7xl mx-auto w-full py-6 px-4">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between">
                    <Link to={"/"}>
                        <div className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                            <span>{t("login.backToHome")}</span>
                        </div>
                    </Link>
                    <LanguageSelector variant="modal" />
                </motion.div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center p-4">
                    {/* Left Side - Information */}
                    <CommonLoginLeftPart />

                    {/* Right Side - Login Form */}

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}>
                        <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur" color={undefined}>
                            <CardHeader className="pb-6" color={undefined}>
                                <div className="md:hidden mb-6 flex justify-center">
                                    <div className="text-2xl font-extrabold text-blue-600 flex items-center gap-3">
                                        <img
                                            src="https://assests.netlify.app/assets/images/logo.png"
                                            alt="Kokobeo Logo"
                                            className="w-10 h-10 object-contain"
                                        />
                                        <span>Kokobeo</span>
                                    </div>
                                </div>
                                <CardTitle className="text-2xl font-bold text-center text-gray-900" color={undefined}>
                                    {t("login.title")}
                                </CardTitle>
                                <p className="mt-2 text-sm text-gray-600 text-center">
                                    {t("login.subtitle")}
                                </p>
                            </CardHeader>
                            {Object.keys(errors).length > 0 && (
                                <div className="w-full flex justify-center">
                                    <div className="w-[88%] my-4 bg-red-100 rounded-md px-3 py-2 flex items-center">
                                        <CircleAlert className="text-red-500 w-4 h-4" />
                                        <p className="text-red-600 text-sm pl-2">{String(Object.values(errors)[0])}</p>
                                    </div>
                                </div>
                            )}
                            <CardContent className="space-y-6">
                                {/* Social Login Buttons */}
                                <div className="space-y-4">
                                    <SocialLogins />
                                </div>

                                {/* Divider */}
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-4 text-gray-500 bg-white">{t("login.orContinueWithEmail")}</span>
                                    </div>
                                </div>

                                {/* Email Field */}
                                <div className="space-y-2">
                                    <label htmlFor="email" className="text-sm font-medium text-gray-700 block">
                                        {t("login.email")}
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder={t("login.emailPlaceholder")}
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-4 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    </div>
                                </div>

                                {/* Password Field */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                            {t("login.password")}
                                        </label>
                                        <Link to={"/auth/forgot-password"}>
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                className="text-sm text-blue-600 hover:text-blue-700 hover:underline">
                                                {t("login.forgotPassword")}
                                            </motion.button>
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder={t("login.passwordPlaceholder")}
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-12 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5" />
                                            ) : (
                                                <Eye className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Login Button */}
                                <motion.button
                                    onClick={handleSubmit}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full h-12 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                    transition-colors font-medium flex items-center justify-center gap-2">
                                    {isLoading ? (
                                        <>
                                            <Spinner size={5} />
                                            {t("login.loading")}
                                        </>
                                    ) : (
                                        t("login.loginButton")
                                    )}
                                </motion.button>

                                {/* Registration Link */}
                                <p className="text-sm text-center text-gray-600">
                                    {t("login.noAccount")}{" "}
                                    <Link to={"/auth/register"}>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
                                            {t("login.signUp")}
                                        </motion.button>
                                    </Link>
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default LoginComponent;
